import { Scene } from '@babylonjs/core/scene';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Color4 } from '@babylonjs/core/Maths/math.color';
import { CreateLines } from '@babylonjs/core/Meshes/Builders/linesBuilder';
import { LinesMesh } from '@babylonjs/core/Meshes/linesMesh';
import { ParticleSystem } from '@babylonjs/core/Particles/particleSystem';
import { PhysicsBody } from '@babylonjs/core/Physics/v2/physicsBody';
import { Ability } from '../../systems/AbilitySystem';
import { COLORS } from '../../config/GameConfig';

/**
 * Pneumatic Anchor — Basil locks his brace and becomes immovable (Babylon.js port).
 *
 * When active:
 * - Basil cannot move (physics body frozen)
 * - Can hold down pressure plates, act as a winch point
 * - Other objects (including Minna) can stand on him
 * - Visual: brass bracket indicators, spark particles
 *
 * Press 3 to engage; press 3 again to release. (Toggle, not hold —
 * solo mode needs Tab + ability keys free while Basil is anchored.)
 */
/**
 * Wounds/Medicine tuning hook. See HarnessWoundSystem. PneumaticAnchor
 * responds to three fields:
 *   - lockedOut — refuse activation (Limping state)
 *   - cooldownScale — longer recovery after release (Cracked/Venting)
 *   - drainScale — multiplies the per-frame drain (applied at the
 *     game-loop integration point since drain math lives in BaseLevel)
 *   - maxHoldSec — auto-release after this many seconds of active hold
 */
export interface PneumaticAnchorTuning {
  drainScale: number;
  cooldownScale: number;
  maxHoldSec: number;
  lockedOut: boolean;
}

export class PneumaticAnchor implements Ability {
  readonly id = 'pneumaticAnchor';
  readonly name = 'Pneumatic Anchor';
  isActive = false;
  cooldownRemaining = 0;
  readonly cooldownDuration = 0.3; // seconds

  private scene: Scene;
  private basilMesh: AbstractMesh;
  private tuningProvider: (() => PneumaticAnchorTuning) | null = null;
  private holdElapsed = 0;

  /** The physics body to freeze (set externally or obtained from mesh) */
  private physicsBody: PhysicsBody | null = null;

  /** Bracket indicator lines */
  private bracketLines: LinesMesh | null = null;

  /** Spark particles */
  private particles: ParticleSystem | null = null;

  /**
   * Mass to restore on deactivate. Captured from the body's current mass
   * the first time activate() runs so we restore the entity-configured
   * value (Basil = 1.5) instead of clobbering it back to a hardcoded 1.
   */
  private restoreMass: number | null = null;

  constructor(scene: Scene, basilMesh: AbstractMesh, physicsBody?: PhysicsBody) {
    this.scene = scene;
    this.basilMesh = basilMesh;
    this.physicsBody = physicsBody ?? null;
  }

  /**
   * Set the physics body reference if not provided in constructor.
   */
  setPhysicsBody(body: PhysicsBody): void {
    this.physicsBody = body;
  }

  /**
   * Resolve the live physics body for Basil's capsule. Prefers the mesh's
   * current `physicsBody` and refreshes the cached ref when the mesh has
   * been re-aggregated (same tolerance logic as OverclockedLeap —
   * guards against cross-level boots via `__startAtPuzzle` handing us
   * a disposed body).
   */
  private getLiveBody(): PhysicsBody | null {
    const meshBody = this.basilMesh.physicsBody ?? null;
    const cachedDisposed = this.physicsBody?.isDisposed === true;
    if (meshBody && (!this.physicsBody || cachedDisposed || meshBody !== this.physicsBody)) {
      this.physicsBody = meshBody;
    }
    return this.physicsBody;
  }

  /**
   * Install a dynamic tuning provider (Wounds/Medicine hook).
   * Null restores the default (no scaling, no max hold).
   */
  setTuningProvider(provider: (() => PneumaticAnchorTuning) | null): void {
    this.tuningProvider = provider;
  }

  private getTuning(): PneumaticAnchorTuning {
    return (
      this.tuningProvider?.() ?? {
        drainScale: 1,
        cooldownScale: 1,
        maxHoldSec: Infinity,
        lockedOut: false,
      }
    );
  }

  /** Current drain scale (read by the BaseLevel energy gate per frame). */
  get drainScale(): number {
    return this.getTuning().drainScale;
  }

  canActivate(): boolean {
    if (this.getTuning().lockedOut) return false;
    return !this.isActive && this.cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    this.isActive = true;
    this.holdElapsed = 0;

    // Freeze the physics body
    const body = this.getLiveBody();
    if (body) {
      // Capture the entity-configured mass on first activation so we can
      // restore it correctly on deactivate. Falls back to 1 if Havok's
      // getMassProperties returns undefined for some reason.
      if (this.restoreMass === null) {
        const props = body.getMassProperties();
        this.restoreMass = (props && typeof props.mass === "number" && props.mass > 0)
          ? props.mass
          : 1;
      }
      body.setLinearVelocity(Vector3.Zero());
      body.setAngularVelocity(Vector3.Zero());
      // Disable motion by setting mass to 0 (makes it kinematic/immovable)
      body.setMassProperties({ mass: 0 });
    }

    // Create bracket indicator
    this.createBracketIndicator();

    // Spark particles — boosted from the original (size 0.03-0.08,
    // rate 30) which was invisible at normal camera distance. Gemini
    // QA flagged "Basil remains in standing idle rather than the
    // locked harness state."
    this.particles = new ParticleSystem('anchorSparks', 120, this.scene);
    this.particles.emitter = this.basilMesh;
    this.particles.minSize = 0.06;
    this.particles.maxSize = 0.18;
    this.particles.minLifeTime = 0.3;
    this.particles.maxLifeTime = 0.8;
    this.particles.emitRate = 60;
    this.particles.minEmitPower = 0.3;
    this.particles.maxEmitPower = 1.5;
    this.particles.direction1 = new Vector3(-0.8, 0.2, -0.8);
    this.particles.direction2 = new Vector3(0.8, 1.2, 0.8);
    this.particles.color1 = new Color4(0.85, 0.68, 0.28, 1); // BRASS
    this.particles.color2 = new Color4(0.80, 0.50, 0.22, 1); // COPPER
    this.particles.colorDead = new Color4(1, 1, 1, 0);
    this.particles.blendMode = ParticleSystem.BLENDMODE_ADD;
    this.particles.createPointEmitter(new Vector3(-0.4, -0.3, -0.4), new Vector3(0.4, 0.4, 0.4));
    this.particles.start();

    return true;
  }

  update(deltaTime: number): void {
    if (!this.isActive) return;

    // Enforce zero velocity while anchored (belt-and-suspenders)
    const body = this.getLiveBody();
    if (body) {
      body.setLinearVelocity(Vector3.Zero());
      body.setAngularVelocity(Vector3.Zero());
    }

    // Update bracket indicator position
    this.updateBracketIndicator();

    // Wounds/Medicine — auto-release if we've exceeded the tuned hold.
    // At Venting the anchor caps at 4s; at Intact / Cracked this is
    // `Infinity` and the check is a no-op.
    this.holdElapsed += deltaTime;
    const { maxHoldSec } = this.getTuning();
    if (Number.isFinite(maxHoldSec) && this.holdElapsed >= maxHoldSec) {
      this.deactivate();
    }
  }

  deactivate(): void {
    this.isActive = false;
    // Wounds/Medicine scales cooldown so a wounded Basil gets a longer
    // "catch your breath" window between anchor presses.
    this.cooldownRemaining = this.cooldownDuration * this.getTuning().cooldownScale;
    this.holdElapsed = 0;

    // Restore physics body to dynamic. We must re-lock the inertia tensor:
    // PlayerEntity sets inertia = Vector3.Zero() to prevent Havok from
    // tumbling the capsule on X/Z, but setMassProperties({mass}) alone lets
    // Havok recompute inertia from the shape, which tips Basil over the
    // moment he moves. Also clear residual angular velocity and snap the
    // orientation back upright. Havok v2 DYNAMIC bodies default to
    // disablePreStep=true, so writing mesh.rotationQuaternion is discarded
    // on the next pre-step unless we flip disablePreStep off for one frame.
    const body = this.getLiveBody();
    if (body) {
      // Restore the entity-configured mass captured at activate-time
      // rather than the previous hardcoded 1, which silently lightened
      // Basil from 1.5 → 1 on every anchor toggle.
      const massToRestore = this.restoreMass ?? 1;
      body.setMassProperties({ mass: massToRestore, inertia: Vector3.Zero() });
      body.setAngularVelocity(Vector3.Zero());
      (body as unknown as { disablePreStep: boolean }).disablePreStep = false;
      if (this.basilMesh.rotationQuaternion) {
        this.basilMesh.rotationQuaternion.set(0, 0, 0, 1);
      } else {
        this.basilMesh.rotation.set(0, 0, 0);
      }
      this.basilMesh.computeWorldMatrix(true);
      this.scene.onAfterRenderObservable.addOnce(() => {
        (body as unknown as { disablePreStep: boolean }).disablePreStep = true;
      });
    }

    // Dispose visuals
    if (this.bracketLines) {
      this.bracketLines.dispose();
      this.bracketLines = null;
    }

    if (this.particles) {
      this.particles.stop();
      this.particles.dispose();
      this.particles = null;
    }
  }

  private createBracketIndicator(): void {
    this.updateBracketIndicator();
  }

  private updateBracketIndicator(): void {
    // Dispose old lines
    if (this.bracketLines) {
      this.bracketLines.dispose();
      this.bracketLines = null;
    }

    const pos = this.basilMesh.position;
    const hw = 1.1; // half-width
    const hh = 1.2; // half-height
    const cornerLen = 0.3;

    const brassColor = Color3.FromHexString(COLORS.BRASS);
    const c = new Color4(brassColor.r, brassColor.g, brassColor.b, 0.9);

    // Build bracket corners as connected line segments
    const points: Vector3[] = [];
    const colors: Color4[] = [];

    // Top-left corner
    points.push(new Vector3(pos.x - hw, pos.y + hh - cornerLen, pos.z));
    points.push(new Vector3(pos.x - hw, pos.y + hh, pos.z));
    points.push(new Vector3(pos.x - hw + cornerLen, pos.y + hh, pos.z));
    // Break to top-right
    points.push(new Vector3(pos.x + hw - cornerLen, pos.y + hh, pos.z));
    points.push(new Vector3(pos.x + hw, pos.y + hh, pos.z));
    points.push(new Vector3(pos.x + hw, pos.y + hh - cornerLen, pos.z));
    // Break to bottom-right
    points.push(new Vector3(pos.x + hw, pos.y - hh + cornerLen, pos.z));
    points.push(new Vector3(pos.x + hw, pos.y - hh, pos.z));
    points.push(new Vector3(pos.x + hw - cornerLen, pos.y - hh, pos.z));
    // Break to bottom-left
    points.push(new Vector3(pos.x - hw + cornerLen, pos.y - hh, pos.z));
    points.push(new Vector3(pos.x - hw, pos.y - hh, pos.z));
    points.push(new Vector3(pos.x - hw, pos.y - hh + cornerLen, pos.z));

    for (let i = 0; i < points.length; i++) {
      colors.push(c);
    }

    this.bracketLines = CreateLines('anchorBrackets', {
      points,
      colors,
      useVertexAlpha: true,
    }, this.scene);
    this.bracketLines.isPickable = false;
  }
}
